import React, { useState, useEffect } from 'react';
import { Search, Download, CheckCircle, XCircle, Heart, Shield, Briefcase, Users, Clock, Trash2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../api';
import { format } from 'date-fns';

export default function AdminUsers() {
    const { showAlert } = useAuth();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [usersData, statsData] = await Promise.all([
                api.getAllUsers(),
                api.getUserStats()
            ]);
            setUsers(usersData);
            setStats(statsData);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const isPartner = user.is_partner === 1 || user.is_partner === true || user.role === 'partner';
        const isAdmin = user.is_admin === 1 || user.is_admin === true || user.role === 'admin';
        
        const matchesRole =
            roleFilter === 'all' ? true :
                roleFilter === 'admin' ? isAdmin :
                    roleFilter === 'partner' ? isPartner :
            /* regular */ !isAdmin && !isPartner;
        return matchesSearch && matchesRole;
    }).filter(user => {
        const isPartner = user.is_partner === 1 || user.is_partner === true || user.role === 'partner';
        const isAdmin = user.is_admin === 1 || user.is_admin === true || user.role === 'admin';
        return isAdmin || isPartner; 
    }); // Hide regular users

    const handleExport = async () => {
        try {
            const blob = await api.exportUsers();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const today = new Date().toISOString().split('T')[0];
            a.download = `users_${today}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            showAlert('Export Failed', 'Failed to export users', 'error');
        }
    };

    const handleSuspend = (userId, shouldSuspend) => {
        const action = shouldSuspend ? 'suspend' : 'activate';
        showAlert(
            shouldSuspend ? 'Suspend User' : 'Activate User',
            `Are you sure you want to ${action} this user?`,
            'warning',
            async () => {
                try {
                    await api.suspendUser(userId, shouldSuspend);
                    showAlert('Success', `User ${action}ed successfully`, 'success');
                    loadData();
                } catch (error) {
                    console.error(`Error ${action}ing user:`, error);
                    showAlert('Error', `Failed to ${action} user: ` + error.message, 'error');
                }
            },
            true, // show cancel
            shouldSuspend ? 'Suspend' : 'Activate'
        );
    };

    const handleDelete = (userId) => {
        showAlert(
            'Delete User',
            'Are you sure you want to PERMANENTLY delete this user? This action cannot be undone.',
            'error',
            async () => {
                try {
                    await api.deleteUser(userId);
                    showAlert('Deleted', 'User deleted successfully', 'success');
                    loadData();
                } catch (error) {
                    console.error('Error deleting user:', error);
                    showAlert('Delete Failed', 'Failed to delete user: ' + (error.detail || error.message), 'error');
                }
            },
            true,
            'Delete Permanently'
        );
    };

    const handleResetPassword = (userId, userName) => {
        showAlert(
            'Reset Password',
            `Reset password for ${userName}? A new temporary password will be generated.`,
            'warning',
            async () => {
                try {
                    const result = await api.resetPartnerPassword(userId);
                    const tempPw = result?.temp_password || result?.temporary_password;
                    if (tempPw) {
                        showAlert('Password Reset!', `Temporary password for ${userName}:\n\n${tempPw}\n\nShare this with the partner — they will be prompted to change it on next login.`, 'success');
                    } else {
                        showAlert('Email Sent', `Password reset email sent to ${userName}`, 'success');
                    }
                    loadData();
                } catch (error) {
                    console.error('Reset password failed:', error);
                    showAlert('Reset Failed', 'Failed to reset password: ' + error.message, 'error');
                }
            },
            true,
            'Confirm Reset'
        );
    };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>User Management</h1>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        View and manage registered users
                    </p>
                </div>
                <button
                    onClick={handleExport}
                    style={{ 
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 20px', borderRadius: '12px',
                        background: 'white', border: '1px solid var(--v2-border)',
                        color: 'var(--v2-text-main)', fontWeight: '600', fontSize: '14px',
                        cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                >
                    <Download size={16} /> Export to Excel
                </button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Total Users
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary)' }}>
                            {stats.total_users}
                        </div>
                    </div>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Active Users
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--success)' }}>
                            {stats.active_users}
                        </div>
                    </div>
                    <div className="admin-card-premium" style={{ padding: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontSize: '14px' }}>
                            Admins
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--accent)' }}>
                            {stats.admin_users}
                        </div>
                    </div>
                </div>
            )}

            {/* Role Filter */}
            <div style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                    { key: 'all', label: 'All Users' },
                    { key: 'partner', label: 'Partners', icon: <Briefcase size={13} /> },
                    { key: 'admin', label: 'Admins', icon: <Shield size={13} /> },
                ].map(f => (
                    <button
                        key={f.key}
                        onClick={() => setRoleFilter(f.key)}
                        className={roleFilter === f.key ? 'btn-premium btn-premium-primary' : 'btn-premium btn-premium-secondary'}
                        style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        {f.icon}{f.label}
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="admin-search-wrapper" style={{ maxWidth: '400px', marginBottom: '24px' }}>
                <Search size={18} className="admin-search-icon" />
                <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-search-input"
                />
            </div>

            {/* Users List */}
            {loading ? (
                <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-muted)' }}>Loading users...</p>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                    <Users size={48} style={{ color: 'var(--text-muted)', margin: '0 auto 16px' }} />
                    <p style={{ color: 'var(--text-muted)' }}>No users found</p>
                </div>
            ) : (
                <div className="admin-table-container" style={{ overflowX: 'auto' }}>
                    <table className="admin-table-premium">
                        <thead>
                                <tr>
                                    <th>User Details</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Platform Activity</th>
                                    <th>Legal</th>
                                    <th>Joined</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div style={{ fontWeight: '700', color: 'var(--v2-text-main)', fontSize: '15px' }}>{user.name}</div>
                                            <div style={{ color: 'var(--v2-text-muted)', fontSize: '13px', marginTop: '2px' }}>
                                                {user.email}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="status-tag role" style={{ 
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                background: (user.is_admin === 1 || user.is_admin === true || user.role === 'admin') ? 'var(--v2-primary-soft)' : 'var(--v2-border-light)',
                                                color: (user.is_admin === 1 || user.is_admin === true || user.role === 'admin') ? 'var(--v2-primary)' : 'var(--v2-text-muted)'
                                            }}>
                                                {(user.is_admin === 1 || user.is_admin === true || user.role === 'admin') ? <Shield size={12} /> : (user.is_partner === 1 || user.is_partner === true || user.role === 'partner') ? <Briefcase size={12} /> : <Users size={12} />}
                                                {(user.is_admin === 1 || user.is_admin === true || user.role === 'admin') ? 'Admin' : (user.is_partner === 1 || user.is_partner === true || user.role === 'partner') ? 'Partner' : 'User'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-tag ${user.is_suspended ? 'suspended' : 'active'}`}>
                                                {user.is_suspended ? 'Suspended' : 'Active'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#EF4444' }}>
                                                    <Heart size={14} fill={user.favorites_count > 0 ? "#EF4444" : "none"} />
                                                    <span style={{ fontSize: '14px', fontWeight: '600' }}>{user.favorites_count}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: user.agreed_to_terms ? '#10b981' : 'var(--v2-text-muted)' }}>
                                                {user.agreed_to_terms ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                                <span style={{ fontSize: '13px', fontWeight: '500' }}>{user.agreed_to_terms ? 'T&C Agreed' : 'Pending'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '14px', color: 'var(--v2-text-main)', fontWeight: '500' }}>
                                                {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                {!(user.is_admin === 1 || user.is_admin === true || user.role === 'admin') && (
                                                    <>
                                                        {(user.is_partner === 1 || user.is_partner === true || user.role === 'partner') && (
                                                            <button 
                                                                onClick={() => handleResetPassword(user.id, user.name)}
                                                                className="table-action-btn edit"
                                                                title="Reset Password"
                                                            >
                                                                <Clock size={16} />
                                                            </button>
                                                        )}
                                                        <button 
                                                            onClick={() => handleSuspend(user.id, !user.is_suspended)}
                                                            className="table-action-btn"
                                                            style={{ color: user.is_suspended ? '#10B981' : '#F59E0B' }}
                                                            title={user.is_suspended ? 'Activate User' : 'Suspend User'}
                                                        >
                                                            {user.is_suspended ? <CheckCircle size={18} /> : <XCircle size={18} />}
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(user.id)}
                                                            className="table-action-btn delete"
                                                            title="Delete User"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                </div>
            )}
        </div>
    );
}
